/**
 * Purpose: Selectable card for choosing a game mode on the Home screen.
 * Owns: Mode name, description, visual selection state.
 * Notes: Designed for the Home screen mode-selection grid. Each card is a large
 *        tap target (CS-13 accessibility). Highlights when selected. Uses theme
 *        tokens exclusively (CS-22).
 */

import type { GameMode } from '../../types/game';

interface ModeCardProps {
  /** Internal game mode ID */
  modeId: GameMode;
  /** Display label (e.g., "Race") */
  label: string;
  /** 1-2 sentence description */
  description: string;
  /** Whether this card is the currently selected mode */
  selected: boolean;
  /** Called when the card is tapped */
  onSelect: (modeId: GameMode) => void;
}

export function ModeCard({
  modeId,
  label,
  description,
  selected,
  onSelect,
}: ModeCardProps) {
  return (
    <button
      style={{
        ...styles.card,
        ...(selected ? styles.cardSelected : {}),
      }}
      onClick={() => onSelect(modeId)}
      aria-pressed={selected}
      aria-label={`Select ${label} mode`}
    >
      <span style={styles.label}>{label}</span>
      <span style={styles.description}>{description}</span>
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    padding: '1.1rem 1.25rem',
    borderRadius: '12px',
    backgroundColor: 'var(--bg-surface)',
    border: '2px solid transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
  },
  cardSelected: {
    border: '2px solid var(--accent-gold)',
    backgroundColor: 'rgba(212, 168, 67, 0.1)',
  },
  label: {
    fontSize: '1.2rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    fontFamily: 'var(--font-family-display)',
  },
  description: {
    fontSize: '0.85rem',
    color: 'var(--muted)',
    lineHeight: '1.4',
  },
};
